import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { operations } from 'store/experiencePlayerManager'
import {
  selectLink,
  selectIsCreating,
  selectIsExpired,
  selectError,
  selectTimeUntilExpiry,
} from 'store/experiencePlayerManager/selectors'
import { POLL_INTERVAL_MS } from 'lib/experiencePlayer/constants'

export default function useExperiencePlayerLaunch({ lessonId }) {
  const dispatch = useDispatch()
  const link = useSelector(selectLink)
  const isCreating = useSelector(selectIsCreating)
  const isExpired = useSelector(selectIsExpired)
  const error = useSelector(selectError)
  const timeUntilExpiry = useSelector(selectTimeUntilExpiry)

  const pollRef = useRef(null)
  const lessonIdRef = useRef(lessonId)
  lessonIdRef.current = lessonId

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  const startPolling = useCallback(
    linkId => {
      stopPolling()
      pollRef.current = setInterval(() => {
        dispatch(operations.checkLinkStatus({ linkId }))
      }, POLL_INTERVAL_MS)
    },
    [dispatch, stopPolling],
  )

  useEffect(() => {
    const stored = dispatch(operations.hydrateFromLocalStorage({ lessonId }))
    if (stored?.linkId) {
      dispatch(operations.checkLinkStatus({ linkId: stored.linkId })).then(
        result => {
          if (result?.isValid) {
            startPolling(stored.linkId)
          }
        },
      )
    }

    return stopPolling
  }, [lessonId, dispatch, startPolling, stopPolling])

  const generateLink = useCallback(
    async ({ lessonId: overrideLessonId, ttlSeconds, generatePassword } = {}) => {
      try {
        const result = await dispatch(
          operations.createLinkForLesson({
            lessonId: overrideLessonId || lessonIdRef.current,
            ttlSeconds,
            generatePassword,
          }),
        )
        if (result?.linkId) {
          startPolling(result.linkId)
        }
        return result
      } catch {
        return null
      }
    },
    [dispatch, startPolling],
  )

  return {
    link,
    isCreating,
    isExpired,
    error,
    generateLink,
    timeUntilExpiry,
  }
}
